// Thin GitHub REST wrapper. Design packages are committed atomically via the
// Git Data API (blobs/trees/commits) — never the Contents API's one-PUT-per-
// file, which can't express "one commit for a whole directory" (see docs
// plan "Design Packages" § Git). Content is passed through as either plain
// utf-8 text or plain base64 text — GitHub's blob API accepts either
// encoding directly, so no extra byte-encoding layer is needed here.

const BASE = 'https://api.github.com';

function headers(token) {
    return {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
        'Content-Type': 'application/json',
    };
}

async function req(url, token, init) {
    // Every multi-step push/delete (getRef -> getCommit -> getTree -> ...
    // -> createCommit -> updateRef) depends on getRef() returning the TRUE
    // current HEAD. fetch()'s default cache mode honors the browser's HTTP
    // cache (GitHub's API responses are cacheable for a short window), so
    // without this a ref read immediately after another tab/operation moved
    // the branch (e.g. a delete) can be served stale — producing exactly a
    // "not a fast forward" error on the next push, since the commit gets
    // built on a parent that's no longer the actual HEAD. Every request here
    // always hits the network.
    const res = await fetch(url, { ...init, headers: headers(token), cache: 'no-store' });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
    }
    return res.status === 204 ? null : await res.json();
}

export async function testConnection({ token, owner, repo }) {
    return req(`${BASE}/repos/${owner}/${repo}`, token);
}

/** The branch's current head commit sha. */
export async function getRef({ token, owner, repo, branch }) {
    const data = await req(`${BASE}/repos/${owner}/${repo}/git/ref/heads/${encodeURIComponent(branch)}`, token);
    return data.object.sha;
}

/** A commit's own tree sha (the root tree at that commit). */
export async function getCommit({ token, owner, repo }, commitSha) {
    return req(`${BASE}/repos/${owner}/${repo}/git/commits/${commitSha}`, token);
}

/** `tree.tree` — a flat array of {path, mode, type, sha, size} when recursive. */
export async function getTree({ token, owner, repo }, treeSha, recursive = false) {
    const qs = recursive ? '?recursive=1' : '';
    return req(`${BASE}/repos/${owner}/${repo}/git/trees/${treeSha}${qs}`, token);
}

/** Fetches one blob's content, decoded to a string (base64 content is left base64-encoded). */
export async function getBlob({ token, owner, repo }, sha) {
    const data = await req(`${BASE}/repos/${owner}/${repo}/git/blobs/${sha}`, token);
    if (data.encoding === 'base64') {
        return { content: data.content.replace(/\n/g, ''), encoding: 'base64' };
    }
    return { content: data.content, encoding: 'utf8' };
}

/** Creates a blob from already-encoded content (`encoding`: 'utf-8' | 'base64'). Returns its sha. */
export async function createBlob({ token, owner, repo }, content, encoding) {
    const data = await req(`${BASE}/repos/${owner}/${repo}/git/blobs`, token, {
        method: 'POST',
        body: JSON.stringify({ content, encoding }),
    });
    return data.sha;
}

/**
 * Creates a tree. `entries`: [{ path, sha }] (sha: null deletes that path).
 * Every entry is a blob at mode 100644 — packages don't need submodules/symlinks.
 */
export async function createTree({ token, owner, repo }, baseTreeSha, entries) {
    const body = { tree: entries.map(e => ({ path: e.path, mode: '100644', type: 'blob', sha: e.sha })) };
    if (baseTreeSha) body.base_tree = baseTreeSha;
    const data = await req(`${BASE}/repos/${owner}/${repo}/git/trees`, token, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return data.sha;
}

export async function createCommit({ token, owner, repo }, message, treeSha, parentSha) {
    const data = await req(`${BASE}/repos/${owner}/${repo}/git/commits`, token, {
        method: 'POST',
        body: JSON.stringify({ message, tree: treeSha, parents: parentSha ? [parentSha] : [] }),
    });
    return data.sha;
}

export async function updateRef({ token, owner, repo, branch }, commitSha) {
    return req(`${BASE}/repos/${owner}/${repo}/git/refs/heads/${encodeURIComponent(branch)}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ sha: commitSha, force: false }),
    });
}

/** Gets commit history for a path (file or directory), up to 30 commits. */
export async function getFileHistory({ token, owner, repo, branch }, path) {
    return req(
        `${BASE}/repos/${owner}/${repo}/commits?path=${encodeURIComponent(path)}&sha=${encodeURIComponent(branch)}&per_page=30`,
        token,
    );
}
